/**
 * /ops/access-review - the quarterly access review (OCP-755). The snapshot OCP
 * collected (operators, grants, OAuth clients, hosts, SSH keys, hints), the
 * external systems a person checks with a note each, and completion with the
 * findings. Past reviews below as the record.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface SystemCheck {
  system: string;
  checked: boolean;
  note: string | null;
  checkedBy: string | null;
  checkedAt: string | null;
}
interface Snapshot {
  collectedAt: string;
  operators: Array<{ email: string; kind: string; role: string; isActive: boolean; mfa: boolean; lastLoginAt: string | null; activeSessions: number }>;
  botGrants: Array<{ bot: string; label: string; clientId: string; expiresAt: string; lastUsedAt: string | null }>;
  oauthClients: Array<{ clientId: string; name: string; lastUsedAt: string | null }>;
  hosts: Array<{ hostname: string; role: string; sshUser: string; ocpAuth: string; passwordLogin: string; keyOnlySince: string | null; authorizedKeys: string[] | null }>;
  operatorSshKeys: Array<{ type: string | null; label: string | null }>;
  findingsHints: string[];
}
interface Review {
  id: string;
  period: string;
  status: 'open' | 'completed';
  opened_at: string;
  opened_by: string;
  snapshot: Snapshot;
  systems: SystemCheck[];
  completed_at: string | null;
  completed_by: string | null;
  findings: string | null;
}
interface Overview {
  current: Review | null;
  items: Array<{ id: string; period: string; status: string; opened_at: string; completed_at: string | null; completed_by: string | null }>;
}

export function AdminAccessReviewPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [findings, setFindings] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api<Overview>('/v1/admin/ops/access-reviews'));
    } catch (e) {
      setErr(e instanceof ApiError ? `Could not load (HTTP ${e.status}).` : 'Could not load.');
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function call(path: string, body: unknown) {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api(path, { method: 'POST', body });
      await load();
    } catch (e) {
      const b = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
      setErr(b?.error === 'review_needs_a_person' ? 'Only a person can check systems and complete the review.' : b?.message ?? 'Request failed.');
    } finally {
      setBusy(false);
    }
  }

  const r = data?.current ?? null;
  const allChecked = !!r && r.systems.every((s) => s.checked);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Access review</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Every quarter: who and what can reach the control plane, the fleet and the external accounts. Runbook: deploy/runbooks/operator-access.md.</p>
        </div>
        <button type="button" disabled={busy} onClick={() => void call('/v1/admin/ops/access-reviews', {})} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm disabled:opacity-50" data-testid="open-review">
          Open this quarter's review
        </button>
      </div>
      {err && <p role="alert" className="text-sm text-red-700 dark:text-red-400">{err}</p>}

      {r && (
        <div className="space-y-5" data-testid="review">
          <p className="text-sm">
            <span className="font-medium">{r.period}</span> · {r.status === 'completed' ? <span className="text-green-700 dark:text-green-400">completed {r.completed_at ? fmtDateTime(r.completed_at) : ''} by {r.completed_by}</span> : <span className="text-amber-700 dark:text-amber-400">open since {fmtDateTime(r.opened_at)}</span>}
          </p>

          {r.snapshot.findingsHints.length > 0 && (
            <div className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm" data-testid="hints">
              <p className="font-medium text-amber-800 dark:text-amber-300">Worth a look</p>
              <ul className="ml-4 list-disc text-amber-800 dark:text-amber-300">
                {r.snapshot.findingsHints.map((h) => <li key={h}>{h}</li>)}
              </ul>
            </div>
          )}

          <Section title={`Operator accounts (${r.snapshot.operators.length})`}>
            <Table head={['Email', 'Kind', 'Role', '2FA', 'Sessions', 'Last login']} rows={r.snapshot.operators.map((o) => [o.email + (o.isActive ? '' : ' (inactive)'), o.kind, o.role, o.kind === 'bot' ? '-' : o.mfa ? 'on' : 'off', String(o.activeSessions), o.lastLoginAt ? fmtDateTime(o.lastLoginAt) : '-'])} />
          </Section>
          <Section title={`Active bot grants (${r.snapshot.botGrants.length})`}>
            <Table head={['Bot', 'Label', 'Client', 'Expires', 'Last used']} rows={r.snapshot.botGrants.map((g) => [g.bot, g.label, g.clientId, fmtDateTime(g.expiresAt), g.lastUsedAt ? fmtDateTime(g.lastUsedAt) : 'never'])} />
          </Section>
          <Section title={`OAuth clients (${r.snapshot.oauthClients.length})`}>
            <Table head={['Name', 'Client id', 'Last used']} rows={r.snapshot.oauthClients.map((c) => [c.name, c.clientId, c.lastUsedAt ? fmtDateTime(c.lastUsedAt) : 'never'])} />
          </Section>
          <Section title={`Fleet hosts (${r.snapshot.hosts.length})`}>
            <Table head={['Host', 'Role', 'OCP auth', 'Password login', 'Key-only since', 'Keys that can log in']} rows={r.snapshot.hosts.map((h) => [h.hostname, h.role, h.ocpAuth, h.passwordLogin, h.keyOnlySince ? fmtDateTime(h.keyOnlySince) : '-', h.authorizedKeys ? h.authorizedKeys.join('; ') : 'run the SSH preflight'])} />
          </Section>
          <Section title="Operator SSH keys">
            <Table head={['Type', 'Label']} rows={r.snapshot.operatorSshKeys.map((k) => [k.type ?? '-', k.label ?? '-'])} />
          </Section>

          <Section title="External systems (checked by a person)">
            <ul className="space-y-2" data-testid="systems">
              {r.systems.map((s) => (
                <li key={s.system} className="rounded border border-gray-200 dark:border-gray-800 p-2 text-sm">
                  <div className="font-medium">{s.system}</div>
                  {s.checked ? (
                    <div className="text-xs text-green-700 dark:text-green-400">Checked {s.checkedAt ? fmtDateTime(s.checkedAt) : ''} by {s.checkedBy}: {s.note}</div>
                  ) : r.status === 'open' ? (
                    <div className="mt-1 flex gap-2">
                      <input value={notes[s.system] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [s.system]: e.target.value }))} placeholder="What you looked at, what you found" className="flex-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-xs" />
                      <button type="button" disabled={busy || (notes[s.system] ?? '').trim().length < 3} onClick={() => void call(`/v1/admin/ops/access-reviews/${r.id}/systems`, { system: s.system, note: (notes[s.system] ?? '').trim() })} className="rounded bg-brand-600 px-2 py-1 text-xs text-white disabled:opacity-50">
                        Checked
                      </button>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">not checked</div>
                  )}
                </li>
              ))}
            </ul>
          </Section>

          {r.status === 'open' ? (
            <div className="space-y-2">
              <textarea value={findings} onChange={(e) => setFindings(e.target.value)} rows={4} placeholder="Findings: what you found and what you changed because of it" className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm" data-testid="findings" />
              <button type="button" disabled={busy || !allChecked || findings.trim().length < 10} onClick={() => void call(`/v1/admin/ops/access-reviews/${r.id}/complete`, { findings: findings.trim() })} className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" data-testid="complete-review" title={allChecked ? '' : 'Check every external system first'}>
                Complete review
              </button>
            </div>
          ) : (
            <Section title="Findings">
              <p className="whitespace-pre-wrap text-sm">{r.findings}</p>
            </Section>
          )}
        </div>
      )}
      {data && !r && <p className="text-sm text-gray-500">No review yet.</p>}

      {data && data.items.length > 1 && (
        <Section title="Past reviews">
          <Table head={['Period', 'Status', 'Opened', 'Completed', 'By']} rows={data.items.map((i) => [i.period, i.status, fmtDateTime(i.opened_at), i.completed_at ? fmtDateTime(i.completed_at) : '-', i.completed_by ?? '-'])} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-1 text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Table({ head, rows }: { head: string[]; rows: string[][] }) {
  if (rows.length === 0) return <p className="text-xs text-gray-500">none</p>;
  return (
    <table className="w-full text-left text-xs">
      <thead className="text-gray-500">
        <tr>{head.map((h) => <th key={h} className="py-1">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {rows.map((row, i) => (
          <tr key={i}>{row.map((c, j) => <td key={j} className="py-1 pr-2 align-top">{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}
